import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';

import { customerService } from '../../../services/api';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import SoftChip from '../../../components/dashboard/SoftChip';
import { scopeLabel } from './columns';
import { permissionsUrl } from '../../../utils/permissionsUrl';

export default function RoleDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const roleQuery = useQuery({
    queryKey: ['role', id],
    queryFn: () => customerService.getRole(id).then((res) => res.data),
  });

  const role = roleQuery.data;

  const handleDelete = async () => {
    await customerService.deleteRole(id);
    queryClient.invalidateQueries({ queryKey: ['roles'] });
    navigate('/roles', {
      state: { seedDelete: { ids: [id], message: t('roles.deleteSuccess') } },
    });
  };

  const header = (
    <PageHeader
      title={role?.name ?? t('roles.detailTitle')}
      breadcrumbs={[
        { label: t('roles.breadcrumb.dashboard') },
        { label: t('nav.groups.team') },
        { label: t('roles.breadcrumb.list'), to: '/roles' },
        { label: role?.name ?? t('roles.detailTitle') },
      ]}
    />
  );

  const renderBody = () => {
    if (roleQuery.isLoading) {
      return <LoadingState message={t('roles.states.loading')} />;
    }

    if (roleQuery.isError) {
      const status = roleQuery.error?.response?.status;
      if (status === 403) {
        return (
          <PermissionDenied
            title={t('roles.states.permissionDeniedTitle')}
            message={t('roles.states.permissionDeniedMessage')}
          />
        );
      }
      if (status === 404) {
        return (
          <ErrorState
            title={t('roles.states.notFoundTitle')}
            message={t('roles.states.notFoundMessage')}
          />
        );
      }
      return (
        <ErrorState
          title={t('roles.states.errorTitle')}
          message={t('roles.states.errorMessage')}
          actionLabel={t('roles.states.retry')}
          onRetry={roleQuery.refetch}
        />
      );
    }

    return (
      <Stack spacing={3} sx={{ width: '100%' }}>
        <Card>
          <CardHeader
            title={t('roles.sections.identification')}
            subheader={t('roles.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              <ListItem disableGutters divider>
                <ListItemText
                  primary={t('roles.fields.name')}
                  secondary={role.name}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
              <ListItem disableGutters divider>
                <ListItemText
                  primary={t('roles.fields.scope')}
                  secondary={scopeLabel(t, role.scope)}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
              <ListItem disableGutters divider>
                <ListItemText
                  primary={t('roles.fields.description')}
                  secondary={role.description || '—'}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary={t('roles.fields.status')}
                  secondary={
                    <SoftChip
                      size="small"
                      label={role.is_active ? t('roles.statuses.active') : t('roles.statuses.inactive')}
                      color={role.is_active ? 'success' : 'default'}
                      sx={{ mt: 0.5 }}
                    />
                  }
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
            </List>
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            {!role.is_owner && (
              <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDelete}>
                {t('roles.actions.delete')}
              </Button>
            )}
            {/* Permissions are edited on their own page (#601). */}
            <Button
              variant="outlined"
              startIcon={<KeyOutlinedIcon />}
              onClick={() => navigate(permissionsUrl({ role: id }))}
            >
              {t('permissions.actions.editPermissions')}
            </Button>
            <Button
              variant="contained"
              startIcon={<EditOutlinedIcon />}
              onClick={() => navigate(`/roles/${id}/edit`)}
            >
              {t('roles.actions.edit')}
            </Button>
          </CardActions>
        </Card>

      </Stack>
    );
  };

  return <BoundedPageLayout header={header}>{renderBody()}</BoundedPageLayout>;
}
